import React, { ReactElement, useEffect, useRef } from 'react';
import Button, { ButtonVariantType } from '@dealroadshow/uikit/core/components/Button';
import { EnabledRedactions } from '@/dataroom/domain/vo/redaction/EnabledRedactions';
import styles from './downloadDropdown.scss';
import Popover from '@dealroadshow/uikit/core/components/Popover';

const downloadButtons = [
  {
    title: 'Download Redacted Document',
    redactionType: EnabledRedactions.WithRedactions,
    dataTest: 'downloadRedactedDocumentButton',
  },
  {
    title: 'Download Without Redaction',
    redactionType: EnabledRedactions.WithoutRedactions,
    dataTest: 'downloadWithoutRedactionDocumentButton',
  },
];

interface IProps {
  handleDownload: ({ redactionType }?: { redactionType?: EnabledRedactions }) => void,
  children: ReactElement,
  isVisible: boolean,
  afterClose?: () => void,
}

const DownloadDropdown = ({
  handleDownload,
  isVisible,
  children,
  afterClose,
}: IProps) => {
  const downloadDropdownRef = useRef(null);

  useEffect(() => {
    // Fix for multiple dropdowns being open
    const handleClickOutside = ({ target }: MouseEvent) => {
      if (!(target as HTMLElement).closest('[data-test="downloadDropdown"]')) {
        downloadDropdownRef.current?.hide();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const onClick = (redactionType: EnabledRedactions) => {
    handleDownload({ redactionType });
    downloadDropdownRef.current.hide();
  };

  const content = (
    <div data-test="downloadDropdown">
      { downloadButtons.map(({ title, redactionType, dataTest }) => (
        <Button
          key={ title }
          title={ title }
          className={ styles.dropdownButton }
          variant={ ButtonVariantType.text }
          dataTest={ dataTest }
          onClick={ () => onClick(redactionType) }
        />
      )) }
    </div>
  );

  return (
    <Popover
      ref={ downloadDropdownRef }
      content={ content }
      triggerEvent="click"
      disabled={ !isVisible }
      renderOnlyVisible
      afterClose={ afterClose }
      arrowClassName={ styles.arrow }
      contentClassName={ styles.dropdown }
    >
      { children }
    </Popover>
  );
};

export default DownloadDropdown;
